// src/utils/firebase/showcase.js
import { 
  deleteDoc,
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL,
  listAll,
  deleteObject
} from 'firebase/storage';
import { db, storage } from './firebase.utils';
import { setDreamTeam } from '../../store/showcase/showcase.action';

const toIsoString = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (typeof value.toISOString === 'function') return value.toISOString();
  return null;
};

const deleteStorageImage = async (imageURL) => {
  if (!imageURL || String(imageURL).startsWith('blob:')) return;
  try {
    const encodedPath = new URL(imageURL).pathname.split('/o/')[1];
    if (!encodedPath) return;
    const storagePath = decodeURIComponent(encodedPath.split('?')[0]);
    await deleteObject(ref(storage, storagePath));
  } catch {
    // imageURL may be malformed or already deleted
  }
};

const omitUndefined = (value) => Object.fromEntries(
  Object.entries(value).filter(([, entry]) => entry !== undefined)
);

const showcaseCollectionRef = (userID) => collection(db, `users/${userID}/showcase`);

const showcaseDocRef = (userID, cardId) => doc(db, `users/${userID}/showcase/${cardId}`);

/** One indexed read for the card in a slot, instead of the whole showcase. */
const getDocsAtPosition = async (userID, positionId) => {
  if (!positionId) return [];
  const snapshot = await getDocs(query(
    showcaseCollectionRef(userID),
    where('positionId', '==', positionId)
  ));
  return snapshot.docs;
};

const commitWrites = async (operations) => {
  const BATCH_LIMIT = 450;
  for (let index = 0; index < operations.length; index += BATCH_LIMIT) {
    const batch = writeBatch(db);
    operations.slice(index, index + BATCH_LIMIT).forEach((operation) => operation(batch));
    await batch.commit();
  }
};


/**
 * Uploads an image to Firebase Storage
 * @param {string} userID - User ID
 * @param {File} imageFile - Image file to upload
 * @param {string} cardId - Card ID for naming the file
 * @returns {Promise<string>} Download URL of the uploaded image
 */
export const uploadCardImageToStorage = async (userID, imageFile, cardId) => {
  try {
    // Create a unique filename
    const timestamp = Date.now();
    const fileExtension = imageFile.name.split('.').pop();
    const fileName = `card_${cardId}_${timestamp}.${fileExtension}`;
    
    // Create storage reference
    const storageRef = ref(storage, `users/${userID}/showcase/${fileName}`);
    
    // Upload the file
    const snapshot = await uploadBytes(storageRef, imageFile);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image to Firebase Storage:', error);
    throw error;
  }
};

/**
 * Saves card data to showcase collection in Firestore
 * @param {string} userID - User ID
 * @param {Object} cardData - Card data to save
 * @param {string} imageURL - Firebase Storage URL for the image (optional)
 * @returns {Promise<string>} Document ID of the saved card
 */
export const saveCardToShowcase = async (userID, cardData, imageURL = null) => {
  try {
    const cardDataToSave = {
      ...cardData,
      imageURL: imageURL,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    // Add document to showcase collection
    const docRef = await addDoc(showcaseCollectionRef(userID), cardDataToSave);
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving card to showcase:', error);
    throw error;
  }
};

/**
 * Clears a slot without deleting the card or its image.
 * @param {string} userID
 * @param {string} positionId
 * @param {string|null} exceptCardId - Card that is about to take the slot
 */
export const unassignShowcaseCardsAtPosition = async (userID, positionId, exceptCardId = null) => {
  if (!positionId) return;
  const occupants = await getDocsAtPosition(userID, positionId);
  const updatedAt = new Date();
  await commitWrites(
    occupants
      .filter((occupant) => occupant.id !== exceptCardId)
      .map((occupant) => (batch) => {
        batch.update(occupant.ref, { positionId: null, updatedAt });
      })
  );
};

/**
 * Saves a card to the showcase pool. positionId may be null.
 * A card already in the target slot is unassigned and kept.
 * @param {string} userID - User ID
 * @param {Object} cardData - Card data including uploadedImage and imageOrientation
 * @param {string|null} positionId - Position ID for the card, or null to leave it unassigned
 * @returns {Promise<{docId: string, finalCardData: Object}>}
 */
export const saveNewCardToShowcase = async (userID, cardData, positionId = null) => {
  try {
    const slotId = positionId || null;
    const previousImageURL = cardData.imageURL && !String(cardData.imageURL).startsWith('blob:')
      ? cardData.imageURL
      : null;
    let imageURL = previousImageURL;

    if (cardData.uploadedImage) {
      const response = await fetch(cardData.uploadedImage);
      const blob = await response.blob();
      const imageFile = new File([blob], 'card-image.jpg', { type: blob.type });
      imageURL = await uploadCardImageToStorage(userID, imageFile, cardData.id || slotId || 'pool');
      if (previousImageURL && previousImageURL !== imageURL) {
        await deleteStorageImage(previousImageURL);
      }
    }

    const { id, createdAt, ...cardDataForFirestore } = cardData;
    delete cardDataForFirestore.uploadedImage;
    const finalCardData = omitUndefined({
      ...cardDataForFirestore,
      positionId: slotId,
      imageURL,
      createdAt: toIsoString(createdAt) || undefined,
    });

    const updatedAt = new Date();
    const writePayload = omitUndefined({
      ...finalCardData,
      updatedAt,
    });

    let docId = id || null;
    if (slotId) {
      const occupants = await getDocsAtPosition(userID, slotId);
      const operations = occupants
        .filter((occupant) => occupant.id !== id)
        .map((occupant) => (batch) => {
          batch.update(occupant.ref, { positionId: null, updatedAt });
        });

      if (docId) {
        operations.push((batch) => batch.update(showcaseDocRef(userID, docId), writePayload));
      } else {
        const newRef = doc(showcaseCollectionRef(userID));
        docId = newRef.id;
        operations.push((batch) => batch.set(newRef, {
          ...writePayload,
          createdAt: new Date(),
        }));
      }
      await commitWrites(operations);
    } else if (docId) {
      await updateDoc(showcaseDocRef(userID, docId), writePayload);
    } else {
      docId = await saveCardToShowcase(userID, finalCardData, imageURL);
    }

    return {
      docId,
      finalCardData: {
        ...finalCardData,
        id: docId,
        positionId: slotId,
        imageURL,
        createdAt: finalCardData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
  } catch (error) {
    console.error('Error saving new card to showcase:', error);
    throw error;
  }
};

/**
 * Fetches all showcase cards for a user
 * @param {string} userID - User ID
 * @returns {Promise<Object>} Object with document id as keys and card data as values
 */
export const fetchShowcaseCards = async (userID) => {
  try {
    const snapshot = await getDocs(showcaseCollectionRef(userID));
    
    const showcaseCards = {};
    snapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      showcaseCards[docSnapshot.id] = {
        ...data,
        id: docSnapshot.id,
        positionId: data.positionId || null,
        createdAt: toIsoString(data.createdAt),
        updatedAt: toIsoString(data.updatedAt),
      };
    });
    
    return showcaseCards;
  } catch (error) {
    console.error('Error fetching showcase cards:', error);
    throw error;
  }
};

/**
 * Fetches and sets showcase cards in Redux store
 * @param {string} userID - User ID
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Showcase cards data
 */
export const fetchAndSetShowcaseCards = async (userID, dispatch) => {
  try {
    const showcaseCards = await fetchShowcaseCards(userID);
    dispatch(setDreamTeam(showcaseCards, "dreamTeamCardData_MLB"));
    return showcaseCards;
  } catch (error) {
    console.error('Error fetching and setting showcase cards:', error);
    throw error;
  }
};

/**
 * Removes a card from a specific position
 * @param {string} userID - User ID
 * @param {string} positionId - Position ID to clear
 * @returns {Promise<void>}
 */
export const removeShowcaseCardFromPosition = async (userID, positionId) => {
  try {
    const occupants = await getDocsAtPosition(userID, positionId);
    await commitWrites(occupants.map((occupant) => (batch) => {
      batch.delete(occupant.ref);
    }));
    await Promise.all(occupants.map((occupant) => deleteStorageImage(occupant.data().imageURL)));
  } catch (error) {
    console.error('Error removing card from position:', error);
    throw error;
  }
};

/**
 * Deletes one showcase card and its stored image.
 * @param {string} userID
 * @param {string} cardId
 * @param {string|null} imageURL
 */
export const deleteShowcaseCard = async (userID, cardId, imageURL = null) => {
  await deleteDoc(showcaseDocRef(userID, cardId));
  await deleteStorageImage(imageURL);
};

/**
 * Updates a card's position in the showcase collection
 * @param {string} userID - User ID
 * @param {string} positionId - Position ID to update
 * @param {Object|null} cardData - Card data to save or null to remove
 * @returns {Promise<void>}
 */
export const updateShowcasePosition = async (userID, positionId, cardData) => {
  try {
    if (cardData === null) {
      await unassignShowcaseCardsAtPosition(userID, positionId);
      return;
    }

    await unassignShowcaseCardsAtPosition(userID, positionId, cardData.id || null);
    const cardDataWithPosition = omitUndefined({
      ...cardData,
      positionId: positionId,
      updatedAt: new Date().toISOString()
    });

    if (cardData.id) {
      await updateDoc(showcaseDocRef(userID, cardData.id), cardDataWithPosition);
    } else {
      await addDoc(showcaseCollectionRef(userID), cardDataWithPosition);
    }
  } catch (error) {
    console.error('Error updating showcase position:', error);
    throw error;
  }
};

/**
 * Swaps two cards between positions in the showcase collection
 * @param {string} userID - User ID
 * @param {string} position1Id - First position ID
 * @param {Object} card1Data - First card data
 * @param {string} position2Id - Second position ID
 * @param {Object} card2Data - Second card data
 * @returns {Promise<void>}
 */
export const swapShowcaseCards = async (userID, position1Id, card1Data, position2Id, card2Data) => {
  try {
    if (!card1Data?.id || !card2Data?.id) {
      await updateShowcasePosition(userID, position1Id, card1Data);
      await updateShowcasePosition(userID, position2Id, card2Data);
      return;
    }

    const updatedAt = new Date();
    await commitWrites([
      (batch) => batch.update(showcaseDocRef(userID, card1Data.id), omitUndefined({
        ...card1Data,
        positionId: position1Id,
        updatedAt,
      })),
      (batch) => batch.update(showcaseDocRef(userID, card2Data.id), omitUndefined({
        ...card2Data,
        positionId: position2Id,
        updatedAt,
      })),
    ]);
  } catch (error) {
    console.error('Error swapping showcase cards:', error);
    throw error;
  }
};

/**
 * Deletes all showcase data from Firestore and Storage
 * @param {string} userID - User ID
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<void>}
 */
export const deleteAllShowcaseData = async (userID, dispatch) => {
  try {
    // 1. Delete all showcase documents from Firestore
    const showcaseRef = showcaseCollectionRef(userID);
    const snapshot = await getDocs(showcaseRef);
    await commitWrites(snapshot.docs.map((docSnapshot) => (batch) => {
      batch.delete(docSnapshot.ref);
    }));

    // 2. Delete all showcase photos from Storage
    const deleteFolderRecursive = async (folderRef) => {
      try {
        const list = await listAll(folderRef);
        // Delete files in this folder
        await Promise.all(list.items.map((itemRef) => deleteObject(itemRef)));
        // Recurse into subfolders
        await Promise.all(list.prefixes.map((subRef) => deleteFolderRecursive(subRef)));
      } catch (error) {
        // If folder doesn't exist or is empty, that's okay
        if (error.code !== 'storage/object-not-found') {
          console.warn('⚠️ Storage deletion warning:', error.message);
        }
      }
    };
    
    try {
      const showcaseStorageRef = ref(storage, `users/${userID}/showcase`);
      await deleteFolderRecursive(showcaseStorageRef);
      console.log('✅ All showcase photos deleted from Storage');
    } catch (storageError) {
      // Continue even if storage deletion fails (folder might not exist)
      console.log('⚠️ Storage deletion warning:', storageError.message);
    }

    // 3. Clear Redux store
    dispatch(setDreamTeam({}, "dreamTeamCardData_MLB"));
    console.log('✅ Redux store cleared');
  } catch (error) {
    console.error('❌ Error deleting all showcase data:', error);
    throw error;
  }
};
